import { FC } from 'react';

import { formatMoney, toCentsFromString, toMoney } from '@betterment/core';
import { Box, FormikField, Icon, IconButton, Input, Table, Text, ToggleGroup } from '@betterment/samba-react';
import { useFormikContext } from 'formik';

import { cellBorderStyle } from './styles';

import { FormTier } from '../data';

interface TableRowProps {
  index: number;
  removeTier: (index: number) => void;
}

export const TableRow: FC<TableRowProps> = ({ index, removeTier }) => {
  const { values, setFieldValue } = useFormikContext<{ tiers: FormTier[] }>();
  const tierStart = (): string => {
    if (index === 0) {
      return formatMoney(toMoney(0));
    }
    const previousTierEnd = values.tiers[index - 1].tierEnd;
    if (previousTierEnd) {
      return formatMoney(toMoney(toCentsFromString(previousTierEnd.replace(/,/g, ''))), 'defaultOptional');
    }
    // show a default of $0.00
    return formatMoney(toMoney(0));
  };
  const handleFirstFeeConfigToggle = (value: string) => {
    void setFieldValue(`tiers.${index}.firmFee.feeConfigType`, value);
  };
  return (
    <Table.Row css={{ height: 'size.800' }}>
      <Table.Cell css={{ ...cellBorderStyle, textAlign: 'center', verticalAlign: 'middle' }}>
        <Text>{index + 1}</Text>
      </Table.Cell>
      <Table.Cell css={{ ...cellBorderStyle, verticalAlign: 'middle' }}>
        <Box css={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '.75fr 0.25fr 1fr' }}>
          <Text>{tierStart()}</Text>
          <Icon name="RightArrow" size="small" css={{ marginRight: 'space.50' }} />
          <FormikField name={`tiers.${index}.tierEnd`}>
            <FormikField.Control>
              <Input
                spacing="compact"
                leadingSymbol="$"
                mask="number"
                aria-label={`Tier ${index} end`}
                maxLength={15}
              />
            </FormikField.Control>
            <FormikField.ErrorMessage />
          </FormikField>
        </Box>
      </Table.Cell>
      <Table.Cell css={{ ...cellBorderStyle, verticalAlign: 'middle' }}>
        {index === 0 ? (
          <Box css={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '1fr .75fr' }}>
            {values.tiers[0].firmFee.feeConfigType === 'fixedConfiguration' ? (
              <FormikField name={`tiers.${index}.firmFee.feeValue`}>
                <FormikField.Control>
                  <Input spacing="compact" aria-label={`Tier ${index} fee amount`} mask="number" leadingSymbol="$" />
                </FormikField.Control>
                <FormikField.ErrorMessage />
              </FormikField>
            ) : (
              <FormikField name={`tiers.${index}.firmFee.feeValue`}>
                <FormikField.Control>
                  <Input spacing="compact" aria-label={`Tier ${index} fee rate`} mask="number" trailingSymbol="bps" />
                </FormikField.Control>
                <FormikField.ErrorMessage />
              </FormikField>
            )}
            <Box css={{ marginLeft: 'auto' }}>
              <FormikField name={`tiers.${index}.firmFee.feeConfigType`}>
                <FormikField.Control>
                  <ToggleGroup
                    value={`${values.tiers[index].firmFee.feeConfigType}`}
                    spacing="default"
                    type="single"
                    aria-label={`Tier ${index} fee configuration type`}
                    onValueChange={(value: string) => handleFirstFeeConfigToggle(value)}
                    css={{ width: '97px', height: 'size.500' }}
                  >
                    <ToggleGroup.Item value="assetBasedConfiguration" name={`tiers.${index}.firmFee.feeConfigType`}>
                      bps
                    </ToggleGroup.Item>
                    <ToggleGroup.Item value="fixedConfiguration" name={`tiers.${index}.firmFee.feeConfigType`}>
                      $
                    </ToggleGroup.Item>
                  </ToggleGroup>
                </FormikField.Control>
              </FormikField>
            </Box>
          </Box>
        ) : (
          <Box css={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '1fr .75fr' }}>
            <FormikField name={`tiers.${index}.firmFee.feeValue`}>
              <FormikField.Control>
                <Input spacing="compact" aria-label={`Tier ${index} fee rate`} mask="number" trailingSymbol="bps" />
              </FormikField.Control>
              <FormikField.ErrorMessage />
            </FormikField>
            <IconButton
              name="X"
              label={`Remove tier index ${index}`}
              css={{ marginLeft: 'auto' }}
              onClick={() => removeTier(index)}
            />
          </Box>
        )}
      </Table.Cell>
    </Table.Row>
  );
};
